import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Smartphone, ScanLine, Wallet, AlertTriangle, Repeat, CalendarCheck,
  CreditCard as CCIcon, Globe, Sparkles, ChevronDown, Mail, Apple, ArrowRight, BarChart3, Shield, FileText
} from "lucide-react";

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
const isAndroid = () => /android/i.test(navigator.userAgent || "");

export default function HelpPage() {
  const [device, setDevice] = useState("auto");
  const [openSection, setOpenSection] = useState("install");

  useEffect(() => {
    if (isIOS()) setDevice("ios");
    else if (isAndroid()) setDevice("android");
    else setDevice("desktop");
  }, []);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="help-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-violet-300/80 font-semibold">Centro de ayuda</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">Empieza aquí</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Cuatro pasos para dominar Trading Dock en menos de 10 minutos. Empieza por instalar la app en tu teléfono — todo lo demás vive ahí.
        </p>
      </div>

      {/* QUICKSTART RAIL — destacado */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="help-quickstart">
        <QuickStartCard num="01" title="Instala en tu teléfono" desc="3 minutos" onClick={() => setOpenSection("install")} active={openSection === "install"} icon={Smartphone}/>
        <QuickStartCard num="02" title="Tu primer recibo" desc="Foto → registrado" onClick={() => setOpenSection("ocr")} active={openSection === "ocr"} icon={ScanLine}/>
        <QuickStartCard num="03" title="Cuentas y deudas" desc="Crear y gestionar" onClick={() => setOpenSection("accounts")} active={openSection === "accounts"} icon={Wallet}/>
        <QuickStartCard num="04" title="Patrimonio neto" desc="Cómo se calcula" onClick={() => setOpenSection("networth")} active={openSection === "networth"} icon={BarChart3}/>
      </div>

      {/* PWA INSTALL — pinned section */}
      <Section open={openSection === "install"} onToggle={() => setOpenSection(openSection === "install" ? "" : "install")} title="📱 Instala Trading Dock como app en tu teléfono" testid="help-install">
        <p className="text-sm text-slate-300">
          Trading Dock es una <strong>PWA</strong> (web app instalable). No necesitas App Store ni Google Play.
          Una vez instalada se abre <strong>a pantalla completa con ícono propio</strong> — se siente como una app nativa.
        </p>

        <div className="flex gap-2 flex-wrap" data-testid="install-device-tabs">
          {[{k:"ios", label:"iPhone / iPad", icon:Apple}, {k:"android", label:"Android", icon:Smartphone}, {k:"desktop", label:"Escritorio", icon:Globe}].map(d => (
            <button key={d.k} onClick={() => setDevice(d.k)} data-testid={`install-tab-${d.k}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border ${device === d.k ? "bg-violet-500/20 border-violet-500/50 text-violet-100" : "bg-white/5 border-white/10 text-slate-400"}`}>
              <d.icon className="w-3.5 h-3.5"/> {d.label}
            </button>
          ))}
        </div>

        {device === "ios" && (
          <Steps testid="ios-steps" items={[
            "Abre app.thetradingdock.com en Safari (no en Chrome — solo Safari permite instalar PWA en iPhone).",
            "Toca el botón Compartir abajo (cuadrado con flecha hacia arriba).",
            "Desliza y toca \"Añadir a la pantalla de inicio\".",
            "Confirma el nombre \"Trading Dock\" y toca \"Añadir\". Cierra Safari.",
            "Listo: el ícono THE TRADING DOCK® aparece en tu home como cualquier app.",
          ]}/>
        )}
        {device === "android" && (
          <Steps testid="android-steps" items={[
            "Abre app.thetradingdock.com en Chrome.",
            "Chrome detecta la app automáticamente y muestra un banner \"Instalar Trading Dock\". Tócalo.",
            "Si no aparece el banner: toca el menú (⋮ arriba derecha) → \"Instalar aplicación\".",
            "Confirma. El ícono aparece en tu cajón de apps y en home.",
            "Listo: ya puedes abrirla como cualquier app nativa.",
          ]}/>
        )}
        {device === "desktop" && (
          <Steps testid="desktop-steps" items={[
            "Abre app.thetradingdock.com en Chrome, Edge o Brave.",
            "En la barra de direcciones aparece un ícono de instalar (monitor con flecha). Tócalo.",
            "O ve a menú ⋮ → \"Instalar Trading Dock…\".",
            "Confirma. La app abre en su propia ventana sin pestañas del navegador.",
          ]}/>
        )}

        <Callout>
          <strong>Beneficios:</strong> abre 3× más rápido que un navegador, funciona sin barra de URL distrayendo, y carga UI cacheada incluso con red inestable. Las APIs sí necesitan internet.
        </Callout>
      </Section>

      <Section open={openSection === "ocr"} onToggle={() => setOpenSection(openSection === "ocr" ? "" : "ocr")} title="🧾 Tu primer recibo con escaneo IA" testid="help-ocr">
        <p className="text-sm text-slate-300">El OCR usa GPT-5.2 Vision para extraer monto, fecha, comercio, categoría y los últimos 4 dígitos de la tarjeta si están visibles.</p>
        <Steps items={[
          "En el Dashboard, toca \"Escanear\" o el botón + del bottom nav → \"Escanear\".",
          "Toma la foto del voucher / factura, o sube una imagen de la galería.",
          "Espera 3-8 segundos. Aparecen los datos prellenados.",
          "Revisa, corrige si algo está mal y confirma. Si el recibo trae los últimos 4 dígitos y coincide con una de tus tarjetas, te sugiere la cuenta vinculada.",
          "La foto queda archivada — puedes verla luego desde la transacción.",
        ]}/>
      </Section>

      <Section open={openSection === "accounts"} onToggle={() => setOpenSection(openSection === "accounts" ? "" : "accounts")} title="💳 Cuentas, tarjetas y deudas" testid="help-accounts">
        <div className="grid md:grid-cols-3 gap-3">
          <MiniHelp icon={Wallet} title="Cuentas" desc="Ingresa a Perfil → Cuentas → Nueva. Define moneda (DOP/USD/USDT) y saldo inicial. Cada transacción ajusta el balance automáticamente."/>
          <MiniHelp icon={CCIcon} title="Tarjetas" desc="Ve a Tarjetas → Nueva. Para débito necesitas vincular una cuenta (ahí vive el dinero real). Para crédito: banco, límite, día de corte y día de pago."/>
          <MiniHelp icon={AlertTriangle} title="Deudas" desc="Deudas → Nueva. Registra acreedor, monto original, tasa y cuota mensual. Cuando pagas: 4 métodos (cuenta, débito, crédito, efectivo). El abono ajusta balance solo."/>
        </div>
        <Callout>
          <strong>Truco:</strong> si una deuda ya tenía pagos antes de usar la app, registra el saldo actual y el monto pagado por separado. La app calcula el % avance.
        </Callout>
      </Section>

      <Section open={openSection === "networth"} onToggle={() => setOpenSection(openSection === "networth" ? "" : "networth")} title="📊 Cómo se calcula tu patrimonio neto" testid="help-networth">
        <p className="text-sm text-slate-300">El número grande del Dashboard es <strong>tu patrimonio real</strong>, no la suma ingenua de cuentas.</p>
        <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 font-mono text-sm">
          Patrimonio neto = Σ saldos de cuentas − deuda de tarjetas de crédito − deudas activas
        </div>
        <p className="text-sm text-slate-400">Toca <em>"cuentas − tarjetas − deudas"</em> debajo del número para ver el desglose en vivo. Las cuentas en USD/USDT se convierten a DOP con tu tabla de tasas (Perfil → Tasas).</p>
      </Section>

      {/* MORE — features destacadas primero */}
      <div>
        <h2 className="text-lg font-bold mb-3">Funcionalidades destacadas</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <FeatureCard icon={Sparkles} title="Asistente del día" desc="Cada mañana ves: próximos pagos de tarjetas, deudas automáticas pendientes, presupuestos al borde, y tasa USDT/DOP P2P en vivo." to="/"/>
          <FeatureCard icon={Repeat} title="Recurrentes inteligentes" desc="Doméstica, Netflix, electricidad. Si no pagas un mes pasado, la app te genera la deuda automáticamente." to="/recurrentes"/>
          <FeatureCard icon={CalendarCheck} title="Conciliación bancaria" desc="Sube el CSV de tu banco al cerrar el mes. La app cruza tus transacciones e importa lo que falta." to="/conciliacion"/>
          <FeatureCard icon={Globe} title="Multi-divisa real" desc="USDT↔DOP usa la mediana de Binance P2P en vivo. Fiat (USD/EUR/MXN) editable manualmente desde Perfil." to="/perfil"/>
          <FeatureCard icon={FileText} title="Exportar a CSV" desc="Desde Actividad puedes descargar todas tus transacciones para tu contador o tu hoja de cálculo." to="/transacciones"/>
          <FeatureCard icon={Shield} title="Privacidad blindada" desc="Tus datos viven en tu propia base aislada. JWT cifrado, HTTPS, bcrypt. Tú decides si invitas familia." to="/familia"/>
        </div>
      </div>

      {/* CONTACT */}
      <div className="card-surface p-5">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-violet-300 mt-0.5 shrink-0"/>
          <div>
            <div className="font-semibold text-sm">¿No encuentras lo que buscas?</div>
            <div className="text-xs text-slate-400 mt-1">Escríbenos a <a href="mailto:soporte@thetradingdock.com" className="text-violet-300 hover:underline" data-testid="help-email">soporte@thetradingdock.com</a> y te respondemos en menos de 24 horas.</div>
          </div>
        </div>
      </div>

      {/* LEGAL */}
      <div className="text-center text-[11px] text-slate-500 leading-relaxed pt-2" data-testid="help-legal">
        <div>The Trading Dock® es un producto operado por <strong className="text-slate-400">Pryma Wellness Group Inc.</strong>, sociedad constituida en Delaware, Estados Unidos.</div>
        <div className="mt-1">Pagos procesados por Stripe Payments Co. El cargo aparece en tu estado a nombre de Pryma Wellness Group Inc.</div>
      </div>
    </div>
  );
}

function QuickStartCard({ num, title, desc, onClick, active, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={`qs-${num}`}
      className={`text-left p-4 rounded-2xl border transition-all ${active ? "bg-violet-500/15 border-violet-500/40" : "bg-white/[0.03] border-white/5 hover:border-white/15"}`}>
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${active ? "bg-violet-500/30 text-violet-200" : "bg-white/5 text-slate-400"}`}>
          <Icon className="w-4 h-4"/>
        </div>
        <span className={`text-[10px] font-bold tracking-wider ${active ? "text-violet-300" : "text-slate-600"}`}>{num}</span>
      </div>
      <div className="mt-3 text-sm font-bold">{title}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">{desc}</div>
    </button>
  );
}

function Section({ title, open, onToggle, children, testid }) {
  return (
    <div className="card-surface overflow-hidden" data-testid={testid}>
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between text-left">
        <h3 className="text-base font-bold">{title}</h3>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && <div className="px-5 pb-5 space-y-4">{children}</div>}
    </div>
  );
}

function Steps({ items, testid }) {
  return (
    <ol className="space-y-2.5" data-testid={testid}>
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm text-slate-200">
          <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500/20 text-violet-200 text-xs font-bold flex items-center justify-center">{i+1}</span>
          <span className="pt-0.5">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Callout({ children }) {
  return (
    <div className="rounded-xl bg-violet-500/10 border border-violet-500/25 px-4 py-3 text-sm text-slate-200 leading-relaxed">{children}</div>
  );
}

function MiniHelp({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
      <Icon className="w-5 h-5 text-violet-300"/>
      <div className="mt-3 text-sm font-bold">{title}</div>
      <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{desc}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, to }) {
  return (
    <Link to={to} className="block p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all group">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-violet-300"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold flex items-center gap-1.5">{title} <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"/></div>
          <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>
        </div>
      </div>
    </Link>
  );
}
